import type { Rotation } from '../core/types';
import { createCamera, unprojectToPlane } from './camera';

/** Narrowed input for hit-testing — only the fields picking actually reads. */
export type PickableState = {
  size: number;
  heightLimit: number;
  rotation: Rotation;
  bottomPadding?: number;
  /** Camera focal length (FOV zoom); undefined falls back to createCamera's default. */
  zoom?: number;
  /** Camera pitch in degrees from vertical; undefined falls back to createCamera's default. */
  tilt?: number;
};

/** How many tile-lengths past the grid's outer edge pickTile still resolves to a clamped edge position, instead of returning null (see pickTile's doc comment). */
const EDGE_BUFFER_TILES = 2;

/**
 * Maps a canvas-space pointer position back to the logical grid cell under
 * it, or null if outside the grid (including a forgiving buffer zone, see
 * below). Casts a 3D ray from the camera through the screen pixel and
 * intersects it with the grid's height-0 plane (see `unprojectToPlane`),
 * which is the perspective-camera analogue of the old linear-inverse
 * "ignore height, approximate at height 0" behavior — still an
 * approximation (it doesn't account for the actual block height under the
 * cursor), but geometrically correct for that flat plane. No grid-rotation
 * step is needed here: the camera itself is what orbits/rotates now, so the
 * ray-plane hit point is already in unrotated grid space.
 *
 * By default, returns the continuous (fractional) position under the
 * pointer, clamped to the valid cell-center range `[0, size - 1]` so the
 * brush center always stays within the playing area even while continuous.
 * Pass `snapToCenter: true` to restore the legacy behavior of snapping to
 * the nearest whole block (used while the "S" key is held, see main.ts).
 *
 * A `EDGE_BUFFER_TILES`-wide buffer zone extends past the grid's outer
 * edge: within it, the position clamps to the nearest valid edge tile
 * (snap mode) or edge point (continuous mode) instead of returning `null`.
 * This exists because picking ignores height, so the flat height-0 plane
 * the pointer projects onto rarely lines up with what's visually under the
 * cursor once blocks have height — without the buffer, the brush indicator
 * disappears far more easily than it looks like it should (playtesting
 * feedback, iteration 17). Only past the buffer does the pointer position
 * genuinely have no valid target.
 */
export function pickTile(
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  state: PickableState,
  snapToCenter = false,
): { x: number; y: number } | null {
  const camera = createCamera(
    canvasWidth, canvasHeight, state.size, state.heightLimit, state.rotation, state.bottomPadding,
    undefined, state.zoom, state.tilt,
  );
  const hit = unprojectToPlane(camera, canvasX, canvasY, 0);
  if (hit === null) {
    return null;
  }

  const minBound = -EDGE_BUFFER_TILES;
  const maxBound = state.size - 1 + EDGE_BUFFER_TILES;
  if (hit.x < minBound || hit.x > maxBound || hit.y < minBound || hit.y > maxBound) {
    return null;
  }

  if (snapToCenter) {
    const snappedX = clamp(Math.round(hit.x), 0, state.size - 1);
    const snappedY = clamp(Math.round(hit.y), 0, state.size - 1);
    return { x: snappedX, y: snappedY };
  }

  const clampedX = clamp(hit.x, 0, state.size - 1);
  const clampedY = clamp(hit.y, 0, state.size - 1);
  return { x: clampedX, y: clampedY };
}

/**
 * Looks up a rendering height for the (possibly fractional, continuous)
 * grid position `x`/`y`, by rounding to the nearest whole tile *only* for
 * this lookup — the caller's own use of `x`/`y` for horizontal placement is
 * unaffected. Used to anchor the brush indicator (target beam, brush-radius
 * overlay) at the actual height of the block under the cursor, instead of
 * indexing `heights[]` directly with a fractional coordinate (which always
 * silently misses and falls back to 0 — the bug this helper fixes). Clamps
 * to the grid's valid range so out-of-range coordinates never throw or
 * index outside the array.
 */
export function heightAt(heights: number[], size: number, x: number, y: number): number {
  const cx = clamp(Math.round(x), 0, size - 1);
  const cy = clamp(Math.round(y), 0, size - 1);
  return heights[cy * size + cx] ?? 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
